import React, { useCallback, useEffect, useState } from 'react'
import { adminFetch as apiFetch, apiErrorMessage } from '../adminFetch'
import { useAccesAdmin } from '../acces'
import '../ouverture/AdminOuverturePage.css'
import './AdminBetaPage.css'
import { ReglagesProduitBeta, TextesCommunsBeta } from './ReglagesBeta.jsx'

/**
 * Back-office du portail › Bêta : la version de TEST de chaque produit
 * (03/10/2026).
 *
 * Le portail décide, les produits appliquent : le réglage voyage dans les
 * jetons (`semply_beta`, `semply_beta_compte`), recalculé à chaque émission.
 * Un changement prend donc effet à la prochaine connexion ou au prochain
 * renouvellement de jeton — au plus tard un quart d'heure.
 *
 * Comme pour l'ouverture, l'enregistrement du mode est EXPLICITE (bouton) :
 * passer un produit en bêta pour tout le monde coupe ses envois officiels.
 */

const PRODUITS = {
  APP: 'SEmplyApp',
  COMPTA: 'SEmplyCompta',
  PREVI: 'SEmplyPrevi',
  IMMO: 'SEmplyImmo',
}

const MODES = [
  {
    value: 'OFF',
    label: 'Désactivée',
    help: 'Le produit en production, pour tout le monde. Les comptes désignés restent listés, sans effet.',
  },
  {
    value: 'TOUS',
    label: 'Tout le produit',
    help: 'Tous les comptes voient la version bêta. Les comptes désignés sont en plus verrouillés.',
  },
  {
    value: 'COMPTES',
    label: 'Comptes choisis',
    help: 'Seuls les comptes désignés ci-dessous voient la version bêta — et sont verrouillés.',
  },
]

const dtf = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
const formatDate = (value) => (value ? dtf.format(new Date(value)) : '—')

export function AdminBetaPage() {
  const writable = useAccesAdmin().superadmin

  const [etat, setEtat] = useState(null)
  const [brouillons, setBrouillons] = useState({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const appliquer = useCallback((data) => {
    setEtat(data)
    setBrouillons(Object.fromEntries(data.produits.map((p) => [p.produit, p.mode])))
  }, [])

  const load = useCallback(async () => {
    setError('')
    try {
      const res = await apiFetch('/admin/beta')
      if (!res.ok) throw new Error(await apiErrorMessage(res, 'Lecture des réglages impossible.'))
      appliquer(await res.json())
    } catch (e) {
      setError(String(e.message ?? e))
    }
  }, [appliquer])

  useEffect(() => {
    load()
  }, [load])

  const appel = async (chemin, options, succes) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const res = await apiFetch(chemin, {
        ...options,
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res, 'Opération impossible.'))
      appliquer(await res.json())
      if (succes) setNotice(succes)
      return true
    } catch (e) {
      setError(String(e.message ?? e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const enregistrerMode = (produit) =>
    appel(
      `/admin/beta/${produit}`,
      { method: 'PUT', body: JSON.stringify({ mode: brouillons[produit] }) },
      `${PRODUITS[produit]} : réglage enregistré. Il s’applique à la prochaine connexion ou au prochain renouvellement de jeton (au plus tard un quart d’heure).`,
    )

  const enregistrerReglages = (produit, corps) =>
    appel(
      `/admin/beta/${produit}/reglages`,
      { method: 'PUT', body: JSON.stringify(corps) },
      `${PRODUITS[produit]} : réglages enregistrés. Textes et affichage : au prochain chargement de page ; période et blocages : à la prochaine connexion ou au prochain renouvellement de jeton.`,
    )

  const enregistrerTextes = (corps) =>
    appel(
      '/admin/beta/textes',
      {
        method: 'PUT',
        body: JSON.stringify({
          texteBandeau: corps.texteBandeau || null,
          titreModale: corps.titreModale || null,
          texteModale: corps.texteModale || null,
        }),
      },
      'Textes communs enregistrés.',
    )

  const designer = async (produit, event) => {
    event.preventDefault()
    const formulaire = event.currentTarget
    const data = new FormData(formulaire)
    const ok = await appel(
      `/admin/beta/${produit}/comptes`,
      {
        method: 'POST',
        body: JSON.stringify({
          email: String(data.get('email') ?? ''),
          label: String(data.get('label') ?? '') || undefined,
        }),
      },
      `${PRODUITS[produit]} : compte désigné.`,
    )
    if (ok) formulaire.reset()
  }

  const retirer = (compte) =>
    appel(
      `/admin/beta/comptes/${compte.id}`,
      { method: 'DELETE' },
      `${compte.email} n’est plus un compte bêta de ${PRODUITS[compte.produit]}.`,
    )

  if (!etat) {
    return error ? (
      <div className="admin-platform">
        <p className="admin-platform-error">{error}</p>
      </div>
    ) : (
      <div className="admin-loading">Chargement…</div>
    )
  }

  return (
    <div className="admin-platform">
      <h1>Version bêta des produits</h1>

      {error && <p className="admin-platform-error">{error}</p>}
      {notice && <p className="admin-platform-notice">{notice}</p>}
      {!writable && <p className="admin-platform-readonly">Réservé aux superadmins de la suite.</p>}

      <section className="admin-platform-card">
        <h2>Ce que change la bêta</h2>
        <ul className="admin-beta-effets">
          <li>
            Un <strong>bandeau rouge</strong> en haut de chaque page, sur le site comme dans
            l’appli mobile — désactivable, texte modifiable par produit.
          </li>
          <li>
            Un <strong>avertissement à chaque connexion</strong>, à valider pour continuer —
            désactivable, texte modifiable.
          </li>
          <li>
            Un filigrane rouge <strong>« Version test, aucune valeur juridique »</strong> sur chaque
            PDF produit par le produit.
          </li>
          <li>
            SEmplyApp : <strong>plateforme agréée bloquée</strong> (par défaut) et, au choix,
            <strong> validation des factures bloquée</strong>.
          </li>
          <li>
            Une <strong>période</strong> facultative : hors de ses dates, le produit repasse en
            production tout seul.
          </li>
          <li>
            Les <strong>comptes désignés</strong> sont des comptes de démonstration partagés :
            mot de passe, double authentification, moyens de connexion, membres, invitations,
            droits, abonnement et suppression du compte y sont grisés et refusés.
          </li>
        </ul>
        <p className="admin-platform-hint">
          Un compte superadmin ou agent support ne peut pas être désigné : il ouvre le back-office
          et ne se partage pas. Pour un compte partagé, gardez la double authentification
          désactivée : un code à usage unique ne se partage pas.
        </p>
      </section>

      <TextesCommunsBeta textes={etat.textes} enregistrer={enregistrerTextes} writable={writable} busy={busy} />

      {etat.produits.map((reglage) => {
        const { produit, mode, enVigueur, updatedAt } = reglage
        const comptes = etat.comptes.filter((c) => c.produit === produit)
        const brouillon = brouillons[produit] ?? mode
        return (
          <section key={produit} className="admin-platform-card">
            <h2 className="admin-beta-titre">
              {PRODUITS[produit]}
              <span className={`admin-beta-pastille admin-beta-pastille--${enVigueur.toLowerCase()}`}>
                {enVigueur === 'OFF'
                  ? 'Production'
                  : enVigueur === 'TOUS'
                    ? 'Bêta pour tous'
                    : 'Bêta sur comptes choisis'}
              </span>
            </h2>
            {(reglage.debut || reglage.fin) && (
              <p className="admin-platform-hint">
                Période : {reglage.debut ? `du ${formatDate(reglage.debut)}` : 'dès maintenant'}
                {reglage.fin ? ` au ${formatDate(reglage.fin)}` : ', sans fin'}
                {mode !== 'OFF' && enVigueur === 'OFF'
                  ? reglage.debut && new Date(reglage.debut) > new Date()
                    ? ' — programmée, pas encore commencée.'
                    : ' — terminée : le produit est revenu en production.'
                  : '.'}
              </p>
            )}

            <div className="admin-platform-modes">
              {MODES.map((m) => (
                <label key={m.value} className={brouillon === m.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name={`mode-${produit}`}
                    disabled={!writable || busy}
                    value={m.value}
                    checked={brouillon === m.value}
                    onChange={() => setBrouillons({ ...brouillons, [produit]: m.value })}
                  />
                  <span>
                    <strong>{m.label}</strong>
                    <em>{m.help}</em>
                  </span>
                </label>
              ))}
            </div>
            <div className="admin-platform-actions admin-beta-actions">
              <button
                type="button"
                onClick={() => enregistrerMode(produit)}
                disabled={!writable || busy || brouillon === mode}
              >
                Enregistrer
              </button>
              <span className="admin-platform-hint">Dernière modification : {formatDate(updatedAt)}</span>
            </div>

            <ReglagesProduitBeta
              produit={reglage}
              communs={etat.textes}
              enregistrer={(corps) => enregistrerReglages(produit, corps)}
              writable={writable}
              busy={busy}
            />

            <h3 className="admin-beta-sous-titre">Comptes bêta désignés ({comptes.length})</h3>
            {writable && (
              <form className="admin-platform-invite-form" onSubmit={(e) => designer(produit, e)}>
                <input name="email" type="email" required placeholder="Adresse du compte" maxLength={320} />
                <input name="label" placeholder="Libellé (suivi interne)" maxLength={200} />
                <button type="submit" disabled={busy}>
                  Désigner
                </button>
              </form>
            )}
            <table className="admin-platform-table">
              <thead>
                <tr>
                  <th>Compte</th>
                  <th>Libellé</th>
                  <th>Désigné le</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {comptes.length === 0 && (
                  <tr>
                    <td colSpan={4} className="admin-platform-empty">
                      Aucun compte désigné.
                    </td>
                  </tr>
                )}
                {comptes.map((c) => (
                  <tr key={c.id} className={enVigueur === 'OFF' ? 'inactive' : ''}>
                    <td>
                      {c.email}
                      {c.nom ? <em> — {c.nom}</em> : null}
                      {c.mfa ? (
                        <div className="admin-beta-alerte">
                          Double authentification active : les testeurs ne pourront pas se connecter.
                        </div>
                      ) : null}
                    </td>
                    <td>{c.label || '—'}</td>
                    <td>{formatDate(c.createdAt)}</td>
                    <td>
                      {writable && (
                        <button type="button" onClick={() => retirer(c)} disabled={busy}>
                          Retirer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      })}
    </div>
  )
}

export default AdminBetaPage
